import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { yupResolver } from "@hookform/resolvers/yup";
import { useToggle } from "usehooks-ts";
import { object, string } from "yup";

import Button from "components/Button";
import ConfirmationModal from "components/ConfirmationModal";
import { routes } from "constants/routes";
import { VEHICLE_NUMBER_TYPES } from "constants/signup";
import CarrierSelector from "features/main/HaulPay/Shared/CarrierSelector";
import { IPaymentProfile } from "models/HaulPay";
import { connectPaymentProfileAPI } from "services/api/haulpay";
import { selectUserDetails } from "store/user/selector";
import styles from "./style.module.scss";

interface IConnectCarrierForm {
  identifyNumber: string;
}

export default function ConnectHaulPayCarriers() {
  const {
    handleSubmit,
    setValue,
    register,
    getValues,
    formState: { errors, isSubmitting }
  } = useForm<IConnectCarrierForm>({
    resolver: yupResolver(
      object({
        identifyNumber: string().required("This field is required")
      })
    )
  });

  const navigate = useNavigate();
  const [selectedCarrierID, setSelectedCarrierID] = useState("");
  const [selectedCarrierType, setSelectedCarrierType] = useState(VEHICLE_NUMBER_TYPES[0]);
  const [selectedPaymentProfile, setSelectedPaymentProfile] = useState<IPaymentProfile>();
  const [isAlreadyConnectedModalOpen, toggleAlreadyConnectedModalOpen] = useToggle();
  const userDetails = useSelector(selectUserDetails);
  const setPaymentProfile = (item: IPaymentProfile) => {
    setSelectedPaymentProfile(item);
    setValue("identifyNumber", item.company_name);
  };

  const handleConnectCarrier = async () => {
    if (selectedPaymentProfile) {
      if (selectedPaymentProfile.is_connected) {
        if (selectedCarrierType.value === "mc")
          setSelectedCarrierID(selectedPaymentProfile.company_mc);
        else setSelectedCarrierID(selectedPaymentProfile.company_dot);
        toggleAlreadyConnectedModalOpen();
      } else {
        const { data: connectionResult } = await connectPaymentProfileAPI(
          userDetails.factoring_id,
          selectedPaymentProfile.id
        );
        setSelectedPaymentProfile(connectionResult.payment_profile);
        navigate(routes.HAULPAY_BROKER_CARRIERs_NEW_FLOW, {
          state: { type: "create_new_profile", profiles: [connectionResult.payment_profile] }
        });
      }
    }
  };

  return (
    <>
      <div className={styles.haulpayCarrierAddNew}>
        <h3 className="text-xl font-medium">HaulPay Carriers</h3>
        <span className="text-tertiary">
          See if your carrier has an account already setup. Search here using MC or DOT #
        </span>
      </div>

      <form className={styles.connectForm} onSubmit={handleSubmit(handleConnectCarrier)}>
        <h3 className="text-lg font-medium">Connect via MC/DOT #</h3>

        <div className={styles.formContent}>
          <CarrierSelector
            register={register}
            errors={errors}
            getValues={getValues}
            setPaymentProfile={setPaymentProfile}
            selectedCarrierType={selectedCarrierType}
            setSelectedCarrierType={setSelectedCarrierType}
          />

          <Button
            type="submit"
            disabled={!selectedPaymentProfile}
            loading={isSubmitting}
            variant="outline"
            rounded
            className={styles.submitButton}>
            Connect
          </Button>
        </div>
      </form>

      <ConfirmationModal
        content={`The carrier with ${selectedCarrierID} is already connected to you and can be found in the Carriers section`}
        isConfirmModalOpen={isAlreadyConnectedModalOpen}
        toggleConfirmModal={toggleAlreadyConnectedModalOpen}
        isHideCancelButton
        title="Already connected!"
        onOK={toggleAlreadyConnectedModalOpen}
      />
    </>
  );
}
